import { useMemo } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography, useTheme } from "@mui/material";
import { formatPriceEuros, formatSentDate, localizeDecimal } from "src/utils/valut/valutFormatters";
import { useLang, useT } from "src/i18n";
import { amberCtaSx } from "src/components/quote/amberCtaSx";
import { glassDialogSlotProps } from "src/theme/glassDialog";
import { gate2Fallback, resolvePeruutusehdot } from "src/utils/legal/acceptanceCopy";

/**
 * Final confirmation before POST /accept.
 * Re-states the key transparency fields on one surface:
 *   provider name, Y-tunnus, total price incl. VAT, cancellation terms,
 *   validity. Acceptance here notifies the provider; the actual pumping
 *   time (and any binding commitment) is agreed by phone afterwards, so
 *   the CTA caption sets that expectation rather than asserting a
 *   payment obligation.
 *
 * GATE-3 TODO (see design doc §2.6): when the customer is a consumer
 * (asiakasTypeId=2) AND the service may start within 14 days, render a separate
 * "Aloita palvelu peruutusajan kuluessa" consent checkbox above the primary
 * button. Currently always hidden; layout reserves room. NOTE: `availableFrom`
 * is no longer captured by the offer flow (the provider form dropped it and the
 * pumping time is agreed by phone after acceptance), so a future GATE-3 must
 * re-source the service-start signal rather than rely on `availableFrom`.
 */
export default function AcceptConfirmModal({ open, offer, onClose, onConfirm, submitting = false, copy }) {
    const theme = useTheme();
    const { lang } = useLang();
    const t = useT();
    // Defensive fallback — ValuDetail always passes a real copy (from its own
    // useAcceptanceCopy hook), so this only guards a caller that forgets to.
    const fallbackCopy = useMemo(() => gate2Fallback(t), [t]);
    const effectiveCopy = copy ?? fallbackCopy;
    if (!offer) return null;

    // Platform-standard cancellation terms (DB-sourced, falls back to the bundled
    // standard if missing/malformed) — the binding-acceptance surface must show
    // them on the main view (KKV); resolvePeruutusehdot guarantees non-empty text.
    const peruutusehdot = resolvePeruutusehdot(effectiveCopy, t);

    // Route through the shared glass slotProps so the binding-accept confirm gets
    // the same branded rise entrance (bjDialogIn), backdrop blur, and @supports
    // fallback as every other modal — this dialog previously hand-rolled a partial
    // copy that predated the GlassDialog consolidation.
    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            slotProps={glassDialogSlotProps(theme)}
        >
            <DialogTitle>{effectiveCopy.confirmTitle}</DialogTitle>
            <DialogContent>
                <Stack spacing={1.5}>
                    <Box>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>{effectiveCopy.responsibilityLabel}</Typography>
                        <Typography variant="body2">
                            {effectiveCopy.responsibilityBody}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>{effectiveCopy.fieldLabels.provider}</Typography>
                        <Typography variant="body2">{t("valut.acceptConfirmModal.providerFieldValue", { name: offer.asiakasNimi, ytunnus: offer.ytunnus || "—" })}</Typography>
                    </Box>

                    <Box>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>{effectiveCopy.fieldLabels.total}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {t("valut.acceptConfirmModal.totalWithVat", { total: formatPriceEuros(offer.priceCents, offer.vatPercent, lang), vat: localizeDecimal(offer.vatPercent, lang) })}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>{effectiveCopy.fieldLabels.cancellation}</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                            {peruutusehdot.full}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>{effectiveCopy.fieldLabels.validUntil}</Typography>
                        <Typography variant="body2">
                            {offer.validUntil ? t("valut.acceptConfirmModal.validUntilValue", { date: formatSentDate(offer.validUntil, lang) }) : "—"}
                        </Typography>
                    </Box>

                    {/* GATE-3 consent checkbox slot — reserved, hidden in v1.
                        See design doc §2.6 and CLAUDE.md "Legal UX conventions". */}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ flexDirection: "column", gap: 1, p: 2 }}>
                <Button
                    fullWidth
                    variant="contained"
                    sx={[amberCtaSx, { py: 1.5 }]}
                    onClick={onConfirm}
                    disabled={submitting}
                    aria-label={effectiveCopy.ariaLabel}
                >
                    <Stack spacing={0.25} sx={{ alignItems: "center" }}>
                        <span>{effectiveCopy.ctaLabel}</span>
                        <Typography variant="caption" sx={{ opacity: 0.85, textTransform: "none" }}>
                            {effectiveCopy.ctaCaption}
                        </Typography>
                    </Stack>
                </Button>
                <Button fullWidth onClick={onClose} disabled={submitting}>
                    {t("valut.acceptConfirmModal.cancel")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
